import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CopyPlus } from 'lucide-react';

export const DuplicateOrderDialog = ({
  duplicateTarget,
  setDuplicateTarget,
  duplicating,
  handleDuplicateOrder,
  companyProjects,
  formatCurrency,
}) => (
  <Dialog open={!!duplicateTarget} onOpenChange={(open) => { if (!open) setDuplicateTarget(null); }}>
    <DialogContent className="sm:max-w-md border border-zinc-200 rounded-none p-0" data-testid="duplicate-order-dialog">
      <DialogHeader className="p-5 pb-0">
        <DialogTitle className="text-xl font-heading font-bold uppercase tracking-wide">Duplicate Order</DialogTitle>
      </DialogHeader>
      {duplicateTarget && (() => {
        const targetProjects = (companyProjects || []).filter(p => p.project_id !== duplicateTarget.order.project_id);
        const selectedProject = targetProjects.find(p => p.project_id === duplicateTarget.target_project_id);
        const targetPhases = selectedProject?.phases || [];
        return (
          <div className="p-5 space-y-4">
            <p className="text-xs text-zinc-500">
              Creates a copy in another project. Vendor, line items, tax, and freight are copied. Status resets to Draft, PO number cleared, date set to today.
            </p>

            {/* Source order preview */}
            <div className="bg-zinc-50 border border-zinc-200 p-3">
              <p className="text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500 mb-1">Copying</p>
              <div className="text-xs space-y-0.5">
                <p><span className="font-mono text-zinc-500">Vendor:</span> <span className="font-semibold">{duplicateTarget.order.vendor_name || '—'}</span></p>
                <p><span className="font-mono text-zinc-500">Line items:</span> <span className="font-semibold">{duplicateTarget.order.line_items?.length || 0}</span></p>
                <p><span className="font-mono text-zinc-500">Total:</span> <span className="font-semibold">{formatCurrency(duplicateTarget.order.total || 0)}</span></p>
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500 mb-1">Target Project</label>
              {targetProjects.length === 0 ? (
                <p className="text-xs text-zinc-400 italic py-2">No other projects available in this company.</p>
              ) : (
                <select
                  value={duplicateTarget.target_project_id}
                  onChange={(e) => setDuplicateTarget({ ...duplicateTarget, target_project_id: e.target.value, target_phase_id: '' })}
                  data-testid="duplicate-target-project"
                  className="w-full h-10 px-3 border-2 border-black bg-white text-sm font-medium focus:outline-none focus:border-primary"
                  autoFocus
                >
                  <option value="">— Select a project —</option>
                  {targetProjects.map(p => (
                    <option key={p.project_id} value={p.project_id}>{p.name}</option>
                  ))}
                </select>
              )}
            </div>

            {selectedProject && targetPhases.length > 0 && (
              <div>
                <label className="block text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500 mb-1">Target Phase <span className="text-zinc-400 normal-case">(optional)</span></label>
                <select
                  value={duplicateTarget.target_phase_id}
                  onChange={(e) => setDuplicateTarget({ ...duplicateTarget, target_phase_id: e.target.value })}
                  data-testid="duplicate-target-phase"
                  className="w-full h-10 px-3 border-2 border-black bg-white text-sm"
                >
                  <option value="">No phase</option>
                  {targetPhases.map(ph => (
                    <option key={ph.phase_id} value={ph.phase_id}>{ph.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setDuplicateTarget(null)}
                data-testid="duplicate-cancel-btn"
                className="flex-1 h-10 bg-white text-zinc-900 font-bold uppercase border-2 border-black hover:bg-zinc-100"
              >
                Cancel
              </button>
              <button
                onClick={handleDuplicateOrder}
                disabled={duplicating || !duplicateTarget.target_project_id}
                data-testid="duplicate-confirm-btn"
                className="flex-1 h-10 bg-primary text-zinc-900 font-bold uppercase border-2 border-black hover:bg-primary-hover disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <CopyPlus className="w-4 h-4" />
                {duplicating ? 'Duplicating…' : 'Duplicate'}
              </button>
            </div>
          </div>
        );
      })()}
    </DialogContent>
  </Dialog>
);
